import React, { useState, useEffect } from 'react';
import { Form, InputGroup, Button } from 'react-bootstrap';
import { FiCopy, FiLink } from 'react-icons/fi';
import axiosInstance from '../../services/AxiosInstance';
import { toast } from 'react-toastify';

const GenerateLinkForm = ({
  fileId,
  permission = 'view',
  expiryOption = 'never',
  onExpiryOptionChange
}) => {
  const [generatedLink, setGeneratedLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Generate link when component mounts or when expiry option changes
  useEffect(() => {
    if (fileId) {
      generateLink();
    }
  }, [fileId, permission, expiryOption]);

  const generateLink = async () => {
    if (!fileId) return;

    try {
      setLoading(true);
      const response = await axiosInstance.post('/share/generate-link', {
        fileId: fileId,
        permission: permission,
        expiryOption: expiryOption
      });

      if (response.status === 200 || response.status === 201) {
        // Backend provides token as query parameter value - extract token and construct URL
        const token = response.data?.data?.token || response.data?.token;
        const link = response.data?.data?.shareUrl || response.data?.shareUrl || response.data?.data?.link || response.data?.link || response.data?.url || '';
        
        let finalLink = '';
        
        // If backend provides just the token, construct the full URL with query parameter
        if (token) {
          const baseUrl = window.location.origin;
          finalLink = `${baseUrl}/shared?token=${encodeURIComponent(token)}`;
        }
        // If backend provides a full URL, check if it needs conversion
        else if (link) {
          // Check if URL has token in path format: /shared/{token} - convert to query parameter
          const pathTokenMatch = link.match(/\/shared\/([^/?&#]+)/);
          if (pathTokenMatch) {
            const pathToken = pathTokenMatch[1];
            try {
              const urlObj = new URL(link);
              finalLink = `${urlObj.protocol}//${urlObj.host}/shared?token=${encodeURIComponent(pathToken)}`;
            } catch (e) {
              // Relative URL
              finalLink = `/shared?token=${encodeURIComponent(pathToken)}`;
            }
          }
          // If already has query parameter, ensure token is properly encoded
          else if (link.includes('?token=') || link.includes('&token=')) {
            const tokenMatch = link.match(/[?&]token=([^&]+)/);
            if (tokenMatch) {
              try {
                const urlToken = decodeURIComponent(tokenMatch[1]);
                finalLink = link.replace(/[?&]token=[^&]+/, `?token=${encodeURIComponent(urlToken)}`);
              } catch (e) {
                // Already encoded, use as-is
                finalLink = link;
              }
            } else {
              finalLink = link;
            }
          }
          // Use link as-is if no conversion needed
          else {
            finalLink = link;
          }
        }
        
        setGeneratedLink(finalLink);
      }
    } catch (error) {
      console.error('Error generating link:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to generate link';
      toast.error(`❌ ${errorMessage}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!generatedLink) return;

    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      toast.success('✔️ Link copied to clipboard!', {
        position: "top-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error copying link:', error);
      toast.error('❌ Failed to copy link', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  return (
    <Form>
      {/* Expiry Option */}
      <Form.Group className="mb-3">
        <Form.Label className="text-label">
          Expiry Option
        </Form.Label>
        <Form.Select
          value={expiryOption}
          onChange={(e) => onExpiryOptionChange(e.target.value)}
        >
          <option value="1day">1 Day</option>
          <option value="1week">1 Week</option>
          <option value="1month">1 Month</option>
          <option value="never">Never</option>
        </Form.Select>
        <small className="form-text text-muted">
          Select when the shared link should expire
        </small>
      </Form.Group>

      {/* Generated Link */}
      <Form.Group className="mb-3">
        <Form.Label className="text-label">
          Generated Link
        </Form.Label>
        <InputGroup>
          <Form.Control
            type="text"
            value={generatedLink}
            disabled
            placeholder={loading ? 'Generating link...' : 'Link will appear here'}
            readOnly
            style={{
              backgroundColor: '#f8f9fa',
              cursor: 'not-allowed'
            }}
          />
          <Button
            variant="outline-secondary"
            onClick={handleCopyLink}
            disabled={!generatedLink || loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {copied ? (
              <>
                <span>Copied!</span>
              </>
            ) : (
              <>
                <FiCopy />
                <span>Copy</span>
              </>
            )}
          </Button>
        </InputGroup>
        {loading && (
          <small className="form-text text-muted">
            Generating link...
          </small>
        )}
        {generatedLink && !loading && (
          <small className="form-text text-muted">
            <FiLink className="me-1" />
            Share this link with anyone who needs access to the document
          </small>
        )}
      </Form.Group>
    </Form>
  );
};

export default GenerateLinkForm;

